import { intlLocale, t } from "@/lib/i18n"
import { parseDecimal } from "@/lib/formatters"
import type { PeriodSales } from "@/features/staff/reports/types"

/**
 * Display helpers for the reports screen. Everything here is pure and unit
 * tested, because everything here is a place a report can quietly start lying.
 *
 * **Nothing in this file derives a peso figure.** Two functions parse a decimal
 * string — `barPercent` to compute a CSS width and `seriesMax` to pick which row
 * is tallest — and both return layout, never a number anyone reads. Every amount
 * on screen is the exact string the server sent, formatted by `formatMoney`.
 */

const I18N_PREFIX = "ui.react.pages.staff.location.reports.show"

export function tReports(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * The copy the BACKEND owns: `ui.staff.reports.*`.
 *
 * The period label, the preset names and every "this figure does not reconcile
 * with that one" note already exist there, because `Staff::ReportPeriod`, the
 * serializer and the CSV writer all read them. The whole `ui` tree is exported
 * to the frontend bundle, so this screen looks them up instead of keeping a
 * second Spanish copy of a sentence whose accuracy is the whole point. Two
 * spellings of "no cuadra con las ventas" is one spelling too many.
 */
export function tSharedReports(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.staff.reports.${key}`, options)
}

/**
 * A growth ratio as a signed percentage.
 *
 * `null` means the baseline was zero or absent, and it renders as "nuevo" rather
 * than as +100%. Growing from nothing is not a percentage, and printing one
 * invites a comparison that cannot be made — the same convention
 * `Admin::DashboardService#delta_percent` already uses.
 */
export function formatGrowth(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return tReports("comparison_new")
  }

  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: "percent",
      signDisplay: "exceptZero",
      maximumFractionDigits: 1,
    }).format(ratio)
  } catch {
    return `${Math.round(ratio * 1000) / 10}%`
  }
}

export type GrowthTone = "up" | "down" | "flat" | "new"

/**
 * Which mark sits beside the delta. Colour lives in that `size-2` dot and never
 * on the figure — the design system's dot rule, and the reason this returns a
 * tone rather than a class name.
 */
export function growthTone(ratio: number | null | undefined): GrowthTone {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "new"
  if (ratio > 0) return "up"
  if (ratio < 0) return "down"
  return "flat"
}

export type ComparisonBasis = {
  /** Which figure the delta describes. */
  basis: "total" | "average_per_shift"
  ratio: number | null
  currentAmount: string
  previousAmount: string
  currentShiftCount: number
  previousShiftCount: number
}

/**
 * What the comparative is actually a comparison OF.
 *
 * The period is a set of shifts, so `#previous` normally brackets the same
 * number of services and a total-vs-total delta is honest. When the server says
 * the two sides differ by more than one shift, the delta moves onto the average
 * per shift — dividing by shifts and not by days, because a restaurant closed on
 * Mondays has fewer shifts than days and per-day normalization under-reports
 * every week it is closed.
 *
 * `null` when there is no previous period at all: a restaurant's first month has
 * nothing to be measured against, and inventing a baseline of zero would report
 * every opening week as infinite growth.
 */
export function comparisonBasis(sales: PeriodSales): ComparisonBasis | null {
  const previous = sales.previous
  if (!previous) return null

  const onAverage = sales.normalizeOnAverage

  return {
    basis: onAverage ? "average_per_shift" : "total",
    ratio: onAverage ? sales.perShiftGrowthRatio : sales.growthRatio,
    currentAmount: onAverage ? sales.averagePerShift : sales.subtotal,
    previousAmount: onAverage ? previous.averagePerShift : previous.subtotal,
    currentShiftCount: sales.shiftCount,
    previousShiftCount: previous.shiftCount,
  }
}

/**
 * A `YYYY-MM-DD` calendar date, formatted WITHOUT ever crossing a zone.
 *
 * The server already bucketed by the restaurant's wall clock, so this string is
 * the answer — there is no instant left to convert. Handing it to `new Date()`
 * would parse it as UTC midnight and, in every negative-offset zone Menny
 * operates in, render the day before: a Saturday's sales filed under Friday on
 * the one screen whose whole job is saying which day was good.
 */
export function formatDayLabel(isoDate: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate)
  if (!parts) return isoDate

  const [, year, month, day] = parts
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))

  try {
    return new Intl.DateTimeFormat(intlLocale(), {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(date)
  } catch {
    return isoDate
  }
}

/** An hour on the restaurant's wall clock. Zero-padded so the column scans. */
export function formatHourLabel(hour: number): string {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return "—"

  return `${String(hour).padStart(2, "0")}:00`
}

/**
 * A bar segment's width as a percentage of the busiest bucket.
 *
 * This is the only place a decimal string is parsed into a float, and what comes
 * out is a CSS width — never a figure. A bucket with anything in it gets at
 * least a sliver, so a slow Tuesday reads as "a little" rather than as "closed".
 */
export function barPercent(amount: string, max: string): number {
  const value = parseDecimal(amount)
  const ceiling = parseDecimal(max)

  if (!(ceiling > 0) || !(value > 0)) return 0

  return Math.max(2, Math.min(100, Math.round((value / ceiling) * 100)))
}

/** The largest amount in a series, as the exact string it arrived as. */
export function seriesMax<T>(rows: T[], amountOf: (row: T) => string): string {
  return rows.reduce((max, row) => {
    const candidate = amountOf(row)
    return parseDecimal(candidate) > parseDecimal(max) ? candidate : max
  }, "0")
}

/** A share (0..1) as a percentage, for the ABC column. */
export function formatShare(share: number): string {
  if (!Number.isFinite(share)) return "—"

  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(share)
  } catch {
    return `${Math.round(share * 1000) / 10}%`
  }
}
